/* eslint-disable max-len */
import { useCallback, useMemo, useReducer, useRef } from 'react';
import { type GiftVoucherDataFragment } from '../../../../api/fragments/GiftVoucherData';
import { type MobilityApplicationEntrypointContextDataFragment } from '../../../../api/fragments/MobilityApplicationEntrypointContextData';
import { useExtendGiftVoucherJourneyExpiryMutation } from '../../../../api/mutations/extendGiftVoucherJourneyExpiry';
import ApplicationSessionModal from '../../../../components/ApplicationSessionExpireModal';
import NotFoundResult from '../../../../components/results/NotFoundResult';
import { useThemeComponents } from '../../../../themes';
import getApolloErrors from '../../../../utilities/getApolloErrors';
import useActiveState from '../../../../utilities/useActiveStage';
import TimeoutPage from '../../EventApplicationEntrypoint/TimeoutPage';
import { JourneyStage } from '../../FinderApplicationPublicAccessEntrypoint/types';
import AdyenPaymentPage from './AdyenPaymentPage';
import FiservPaymentPage from './FiservPaymentPage';
import InitializeStage from './InitializeStage';
import GiftVoucherPurchaserKYCPage from './KYCPage';
import PayGatePaymentPage from './PayGatePaymentPage';
import PorschePaymentPage from './PorschePaymentPage';
import { reducer } from './reducer';
import ThankYouPage from './ThankYouPage';
import TtbPaymentPage from './TtbPaymentPage';

type JourneyControllerProps = {
    initialToken?: string;
    initialGiftVoucher: GiftVoucherDataFragment;
    initialStage: JourneyStage;
    endpoint: MobilityApplicationEntrypointContextDataFragment;
};

const JourneyController = ({ initialGiftVoucher, initialStage, initialToken, endpoint }: JourneyControllerProps) => {
    const initialState = useRef({
        token: initialToken,
        stage: initialStage,
        giftVoucher: initialGiftVoucher,
    });
    const [state, dispatch] = useReducer(reducer, initialState.current);
    const { StandardLayout, notification } = useThemeComponents();

    // Detect idle state
    const { isActive, reset } = useActiveState();

    const [extendGiftVoucherJourneyExpiration] = useExtendGiftVoucherJourneyExpiryMutation();

    const handleExtendSession = useCallback(async () => {
        try {
            const { data } = await extendGiftVoucherJourneyExpiration({ variables: { token: state.token } });

            if (!data?.result?.giftVoucher) {
                throw new Error('Gift voucher not found');
            }

            if (!data?.result?.token) {
                throw new Error('Token not found');
            }

            dispatch({
                type: 'refresh',
                token: data.result.token,
                giftVoucher: data.result.giftVoucher,
            });
        } catch (error) {
            const apolloErrors = getApolloErrors(error);

            if (apolloErrors !== null) {
                const { $root: rootError } = apolloErrors;

                // Expect this already handled on the outside
                if (rootError && rootError !== 'Session has Expired') {
                    notification.error(rootError);
                }
            } else {
                console.error(error);
            }
        }
    }, [extendGiftVoucherJourneyExpiration, state.token, notification]);

    const journeyStepPage = useMemo(() => {
        switch (state.stage) {
            case JourneyStage.Initialize:
                return <InitializeStage dispatch={dispatch} state={state} />;

            case JourneyStage.ApplicantKYC:
                return <GiftVoucherPurchaserKYCPage dispatch={dispatch} endpoint={endpoint} state={state} />;

            case JourneyStage.Deposit:
                switch (state.giftVoucher.deposit.__typename) {
                    case 'ApplicationAdyenDeposit':
                        return <AdyenPaymentPage CustomLayout={StandardLayout} dispatch={dispatch} state={state} />;

                    case 'ApplicationPorscheDeposit':
                        return <PorschePaymentPage CustomLayout={StandardLayout} dispatch={dispatch} state={state} />;

                    case 'ApplicationFiservDeposit':
                        return <FiservPaymentPage CustomLayout={StandardLayout} dispatch={dispatch} state={state} />;

                    case 'ApplicationPayGateDeposit':
                        return <PayGatePaymentPage CustomLayout={StandardLayout} dispatch={dispatch} state={state} />;

                    case 'ApplicationTtbDeposit':
                        return <TtbPaymentPage CustomLayout={StandardLayout} dispatch={dispatch} state={state} />;

                    default:
                        return <NotFoundResult />;
                }

            default:
                return <NotFoundResult />;
        }
    }, [StandardLayout, endpoint, state]);

    if (!isActive && !state?.giftVoucher?.draftFlow?.isReceived) {
        return <TimeoutPage onBack={reset} />;
    }

    if (state?.giftVoucher?.draftFlow?.isReceived) {
        return <ThankYouPage endpoint={endpoint} state={state} />;
    }

    return (
        <>
            <ApplicationSessionModal expireAt={state.giftVoucher?.expireAt} extendSession={handleExtendSession} />
            {journeyStepPage}
        </>
    );
};

export default JourneyController;
